import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { debounce } from 'lodash'; // We'll need to install lodash

/**
 * Hook for managing project data with auto-save functionality
 */
export const useProject = (projectId = null) => {
  const { currentUser } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load project data
  useEffect(() => {
    if (!currentUser || !projectId) {
      setLoading(false);
      return;
    }

    const projectRef = doc(db, 'projects', projectId);

    const unsubscribe = onSnapshot(
      projectRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = { id: snapshot.id, ...snapshot.data() };
          setProject(data);
          setError(null);
        } else {
          setError('Project not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading project:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, projectId]);

  // Auto-save functionality with debouncing
  const saveProject = useCallback(
    debounce(async (updates) => {
      if (!projectId || !currentUser) return;

      setSaving(true);
      try {
        const projectRef = doc(db, 'projects', projectId);

        // Add timestamp
        const dataToSave = {
          ...updates,
          updatedAt: new Date()
        };

        await updateDoc(projectRef, dataToSave);
        setLastSaved(new Date());
        setSaving(false);
      } catch (err) {
        console.error('Error saving project:', err);
        setError(err.message);
        setSaving(false);
      }
    }, 1000), // 1 second debounce
    [projectId, currentUser]
  );

  // Update specific hub tool data
  const updateHubTool = useCallback((toolName, toolData) => {
    if (!project) return;

    const updatedProject = {
      ...project,
      hubData: {
        ...project.hubData,
        [toolName]: toolData
      }
    };

    // Update local state immediately for responsiveness
    setProject(updatedProject);

    // Save to Firestore (debounced)
    saveProject({
      [`hubData.${toolName}`]: toolData
    });
  }, [project, saveProject]);

  // Add activity
  const addActivity = useCallback((activity) => {
    if (!project) return;

    const newActivity = {
      timestamp: new Date(),
      ...activity
    };

    const updatedActivities = [newActivity, ...(project.hubData.activities || [])];

    updateHubTool('activities', updatedActivities);
  }, [project, updateHubTool]);

  // Create new project (Server-Side Init)
  const createProject = useCallback(async (tier = 'studio', category = 'adu_residential') => {
    if (!currentUser) throw new Error('User not authenticated');

    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../firebase-config');

    // Call the Server-Side Orchestrator
    const initProjectFn = httpsCallable(functions, 'initProject');

    try {
      // Generate a client-side ID for optimistic UI (optional) or let server decide
      // Here we explicitly generate it to track it immediately if needed, 
      // but typical pattern is let server handle or pass ID.
      // We'll pass a UUID so we know where to look immediately.
      const projectId = crypto.randomUUID();

      const result = await initProjectFn({
        projectId,
        category,
        tier
      });

      return projectId;
    } catch (err) {
      console.error('Error creating project:', err);
      throw err;
    }
  }, [currentUser]);

  return {
    project,
    loading,
    error,
    saving,
    lastSaved,
    updateHubTool,
    addActivity,
    createProject,
    reload: () => window.location.reload() // Simple reload for now
  };
};

/**
 * Hook to get current user's active project
 */
export const useCurrentProject = () => {
  const { currentUser } = useAuth();
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const loadCurrentProject = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists() && userDoc.data().currentProjectId) {
          setCurrentProjectId(userDoc.data().currentProjectId);
        }
      } catch (error) {
        console.error('Error loading current project:', error);
      }
      setLoading(false);
    };

    loadCurrentProject();
  }, [currentUser]);

  const projectData = useProject(currentProjectId);

  return {
    ...projectData,
    projectId: currentProjectId,
    loading: loading || projectData.loading
  };
};